import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, KeyRound, CreditCard, Users, LayoutDashboard, Gift, Moon, Sun, AlertTriangle, ShieldCheck, KeySquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './elements/Button';
import Switch from './elements/Switch';
import Loading from './elements/Loading';
import { updateMe } from '../utils/authApi';

const UserDropdown = ({ onLogout, onChangePassword, onSubscription }) => {
  const OVERLAY_COLOR_TRANSITION_MS = 2000;
  const OVERLAY_FADE_OUT_MS = 1000;
  const OVERLAY_HOLD_AFTER_COLOR_MS = 120;

  const [open, setOpen] = useState(false);
  const role = sessionStorage.getItem('role');
  const isSubscribed = sessionStorage.getItem('isSubscribed') === 'true';
  const [userProfile] = useState({
    name: `${sessionStorage.getItem('firstName') || ''} ${sessionStorage.getItem('lastName') || ''}`.trim() || 'User',
    email: sessionStorage.getItem('email') || ''
  });
  const [theme, setTheme] = useState(localStorage.getItem('theme') || sessionStorage.getItem('theme') || 'light');
  const [isThemeUpdating, setIsThemeUpdating] = useState(false);
  const [overlayTheme, setOverlayTheme] = useState(theme);
  const [isOverlayFading, setIsOverlayFading] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const isDarkTheme = theme === 'dark';
  const getOverlayColor = (themeValue) => (themeValue === 'dark' ? '#000000' : '#ffffff');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    sessionStorage.setItem('theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleThemeToggle = async (checked) => {
    if (isThemeUpdating) {
      return;
    }

    const newTheme = checked ? 'dark' : 'light';
    const previousTheme = theme;

    setOverlayTheme(previousTheme);
    setIsOverlayFading(false);
    setIsThemeUpdating(true);

    // Trigger a visible crossfade on the fullscreen overlay between themes.
    requestAnimationFrame(() => setOverlayTheme(newTheme));
    setTheme(newTheme);

    const finishThemeUpdate = () => {
      const fadeStartDelay = OVERLAY_COLOR_TRANSITION_MS + OVERLAY_HOLD_AFTER_COLOR_MS;

      setTimeout(() => {
        setIsOverlayFading(true);
      }, fadeStartDelay);

      setTimeout(() => {
        setIsThemeUpdating(false);
        setIsOverlayFading(false);
      }, fadeStartDelay + OVERLAY_FADE_OUT_MS);
    };

    if (sessionStorage.getItem('isLoggedIn') !== 'true') {
      finishThemeUpdate();
      return;
    }

    if (role !== 'nominee') {
      try {
        const result = await updateMe({ theme: newTheme });
        if (!result.success) {
          setTheme(previousTheme);
        }
      } catch (_error) {
        setTheme(previousTheme);
      } finally {
        finishThemeUpdate();
      }
    } else {
      // For nominees, we won't make an API call but will still show the loading state briefly to indicate the change
      finishThemeUpdate();
    }
  };

  return (
    <>
    <meta name="color-scheme" content={isDarkTheme ? 'dark' : 'light'} />

    <div className="relative font-sans" ref={ref}>
      <Button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex flex-row items-center gap-2 w-full text-left"
      >
        <User className="w-5 h-5 mr-1" />
        <span className="w-full text-left">Account</span>
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 py-1 min-w-[220px] max-w-[90vw] sm:max-w-[30vw] bg-background-dark border border-accent-main rounded-lg shadow-lg z-50">
          <Button
            type='anchor'
            onClick={() => { setOpen(false); navigate('/profile'); }}
            className="w-full"
          >
            <div className="flex flex-col">
              <span className="font-semibold">{userProfile.name}</span>
              <span className="text-sm text-gray-500">View Profile</span>
            </div>
          </Button>
          <hr className="border-accent-main" />

          {isSubscribed && (
            <Button
              type='anchor'
              onClick={() => { setOpen(false); isSubscribed && navigate('/dashboard'); }}
              className="w-full"
              disabled={!isSubscribed}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="w-full text-left">Dashboard</span>
            </Button>
          )}

          {/* Hidden for nominees: the subscription being shown is the OWNER's, and
              the modal's actions (cancel / upgrade) act on it. The server rejects
              those with 403 (mw.isOwnerRole), so leaving the entry visible would
              only offer a nominee a control that errors. */}
          {role !== 'nominee' && onSubscription && (
            <Button
              type='anchor'
              onClick={() => { setOpen(false); isSubscribed ? onSubscription && onSubscription() : navigate('/subscription-check'); }}
              className="w-full"
            >
              <CreditCard className="w-4 h-4" />
              <span className="w-full text-left">Subscription Details</span>
            </Button>
          )}
          
          {role !== 'nominee' && (
            <Button
              type='anchor'
              onClick={() => { setOpen(false); navigate('/referral'); }}
              className="w-full"
            >
              <Users className="w-4 h-4" />
              <span className="w-full text-left">Referral Program</span>
            </Button>
          )}

          {role !== 'nominee' && onChangePassword && (
            <Button
              type='anchor'
              onClick={() => { setOpen(false); onChangePassword && onChangePassword(); }}
              className="w-full"
            >
              <KeyRound className="w-4 h-4" />
              <span className="w-full text-left">Change Password</span>
            </Button>
          )}

          {role === 'admin' && (
            <div className="py-2">
              <hr className="border-accent-main" />
              <span className="px-3 py-1 text-xs text-text-muted uppercase">Admin Panel</span>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-dashboard'); }}
                className="w-full"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="w-full text-left">Dashboard Overview</span>
              </Button>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-users'); }}
                className="w-full"
              >
                <Users className="w-4 h-4" />
                <span className="w-full text-left">Manage Users</span>
              </Button>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-plans'); }}
                className="w-full"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="w-full text-left">Manage Plans</span>
              </Button>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-incidents'); }}
                className="w-full"
              >
                <AlertTriangle className="w-4 h-4" />
                <span className="w-full text-left">Security Incidents</span>
              </Button>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-audit'); }}
                className="w-full"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="w-full text-left">Audit Logs</span>
              </Button>

              <Button
                type='anchor'
                onClick={() => { setOpen(false); navigate('/admin-encryption'); }}
                className="w-full"
              >
                <KeySquare className="w-4 h-4" />
                <span className="w-full text-left">Encryption Keys</span>
              </Button>

            </div>
          )}

          <hr className="border-accent-main" />
          <div className="px-3 py-2 flex gap-2 bg-background-dark text-accent-main">
            <Switch
              checked={isDarkTheme}
              onChange={handleThemeToggle}
              disabled={isThemeUpdating}
              onColor='bg-black'
              offColor='bg-white'
              ballColor={isDarkTheme ? 'bg-white' : 'bg-black'}
            >
              {isDarkTheme ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              Theme
            </Switch>
          </div>

          <hr className="border-accent-main" />
          <Button
            type='anchor'
            onClick={() => { setOpen(false); onLogout && onLogout(); }}
            className="text-red-500 w-full flex flex-row items-center gap-2 text-left"
          >
            <LogOut className="w-4 h-4" />
            <span className="w-full text-left">Logout</span>
          </Button>
        </div>
      )}

      {isThemeUpdating && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{
            backgroundColor: getOverlayColor(overlayTheme),
            opacity: isOverlayFading ? 0 : 1,
            transition: `background-color ${OVERLAY_COLOR_TRANSITION_MS}ms ease, opacity ${OVERLAY_FADE_OUT_MS}ms ease`
          }}
        >
          <div
            className="flex items-center gap-2 px-4 py-3 rounded-md border text-sm shadow-xl"
            style={{
              backgroundColor: overlayTheme === 'dark' ? '#141414' : '#f5f5f5',
              color: overlayTheme === 'dark' ? '#f5f5f5' : '#0f172a',
              borderColor: overlayTheme === 'dark' ? '#334155' : '#cbd5e1',
              transition: 'background-color 450ms ease, color 450ms ease, border-color 450ms ease'
            }}
          >
            <Loading />
            Saving theme...
          </div>
        </div>
      )}
    </div>
    </>

  );
};

export default UserDropdown;
